/**
 * GA4, one door for all of it: page views, events, who the user is.
 *
 * index.html defines gtag() everywhere but only loads Google's script on real
 * hosts (or with ?ga_debug=1), and sets window.__maGaActive when it does. On a
 * dev machine or in Playwright these calls queue into dataLayer and go
 * nowhere. Tracking must never break the app: every call is guarded, empty
 * params are dropped, and nothing personal is sent (ids only, never an
 * email or a name). The full dictionary, with GA admin steps, is in
 * docs/analytics.md.
 *
 * Page views (virtual: the app is mostly "/" with the surface in the query)
 *   page_view           router.afterEach → pageView(); path/title from
 *                       utils/analytics-pages.js. The first one carries the
 *                       landing URL as captured at load, utm_* intact.
 *
 * Accounts
 *   sign_up             {method: email | google | phone_code | onboarding_claim | care_package | care_package_link}
 *   login               {method: email | email_code | phone_code | google | care_package}
 *   logout
 *   login_required_shown {theme}      a gated action asked the visitor to sign in
 *   (user_id is set whenever store.querent_id is, cleared when it empties;
 *    user property is_mystic follows store.isReader)
 *
 * Readings (funnel: reading_intent → card_drawn(1) → reading_start → reading_complete)
 *   reading_intent      {reader, via: profile | community | for_someone | lessons | invite | foot}  tapped Get Reading (or A reading for someone; lessons = the Lessons tab's Ask the cards about it; invite = the feed's invitation card; foot = the feed bottom's Ask pill)
 *   reading_for_someone_start {reader}  tapped "A reading for someone" on the Mystic's page
 *   reading_start       {item_id, reader, reading_type}   once per reading, when the
 *                       server creates the session (after the spread is drawn)
 *   card_drawn          {item_id, reader, position}
 *   reading_complete    {item_id, reader}                  once per reading
 *   reading_rated       {item_id, rating, via?: gift}   via 'gift': a package rated in its room
 *   journal_reflect     {lens}
 *   journal_intention_answer {answer: held | partly | not}  "Did it hold?" answered (never the entry's words)
 *   journal_clasp_on    {passkey: 0 | 1}   the Journal's clasp was set (a PIN, with or without Face ID / Touch ID)
 *   journal_contents_thread {count}      a thread opened from the Journal's contents (count = its readings)
 *   journal_contents_day   a day opened from the Journal's contents calendar
 *   journal_contents_kept  the Kept shelf opened from the Journal's contents
 *   keep_image {source, kept}  Keep tapped on a picture from others (source = mystic_drop | lesson | card)
 *   horoscope_personalized
 *
 * Sharing (the Django share pages label the visit: Mystic-Aida/tarot/service/share_tracking.py)
 *   share_open          {content_type, item_id}   the Share sheet opened
 *   share               {method: copy_link | email | sms | friends | care_package | embed_code,
 *                        content_type: reading | candidate | syndication, item_id}
 *   reading_made_public / reading_made_private {item_id}
 *   share_landing       (Django) a person opened a shared link
 *   shared_reading_open {item_id, via: link | invite | care_package, share_campaign}
 *   invite_accepted     {captured}
 *
 * A lesson from the cards (the public side of a shared reading, 2026-10-25)
 *   lesson_view         {item_id, via: link | community | mystic | related | back}   the lesson page (L2) or its in-app sheet showed (related: a "More about…" lesson opened in place; back: Back/Forward between lessons in the window, 2026-10-28)
 *   lesson_resonated    {item_id}   "This spoke to me" turned on (not when taken back)
 *   lesson_draw         {item_id, how: draw | talk}   the lesson's ending opened a fresh session with its Mystic (2026-10-28): Draw a card, or Talk it over instead (never the sharer's Ask more)
 *   lesson_search       {via: search | topic | foot, topic, how, found}   the Lessons tab's search answered (topic: the Topics line or the fire's stat; foot: the search at the feed's bottom; never the words typed)
 *   lesson_ask          {via: search | foot}   "✦ Ask {Mystic} about it" tapped on a search that found no lesson; foot: the feed bottom's "✦ Ask {Mystic} about “…”"
 *   lesson_invite       {act: shown | meet | ask, topic, reader}   the Lessons feed's invitation card at a page seam (community-feed-invite 2026-10-30): shown = 50% in view for 1s; meet = its portrait or name; ask = ✦ Ask {Mystic}. Topic key and Mystic subdomain only
 *   lesson_share_switch {item_id, state: on | off, first}   the share sheet's "In the Community" switch settled (rev 13, 2026-10-28); first: the first time it went on
 *   lesson_edit         {item_id, how: choice | typed}   new words placed on the card in the share sheet (Done)
 *   lesson_rewrite_choice {item_id, choice: keep | change | private}   the once-only line after the backfill rewrite was answered
 *   coven_share_switch  {item_id, state: on | off}   "Your coven sees it" switched in the share sheet
 *
 * Gift journey (the care package's room)
 *   gift_chip           {chip: gift_deeper | gift_thank | gift_own | gift_stars}
 *   gift_deeper_start   {state: open | drawn}   "Pull a card for where you stand" started (or found drawn)
 *   gift_opener_branch  {branch_index}          she tapped a branch link in the Mystic's opener (0, 1, 2)
 *   gift_card_pulled                            she drew her one card in the room
 *   gift_thanked        {with_note}             the recipient's thank-you reached the sender
 *   gift_keep           {step: sent | confirmed | merged}  "Keep this": her one link sent, then tapped
 *   reading_reflection                          her line to "What landed?" kept on her own reading
 *   gift_person         {via: named | someone_else}  Stage 4: she picked a person from the Mystic's closing turn
 *   gift_password       {step: saved | pending | not_now}  her first send's password moment
 *   gift_preview_open   {lane: member | share | copy}  the sender read it as she will before anything went out (2026-10-21)
 *   introduction_end    {trigger: both_sides | balance}  the end of her introduction, the Mystic's one line (R8.3)
 *   (map_open and map_go retired with the map, 2026-10-26)
 *   recommend_shown     {practice}              the Mystic recommended another voice in the room (R9.4)
 *   recommend_tapped                            she tapped that Mystic's name
 *   quest_line          {quest}                 the Mystic named a quest just earned, in the chat
 *
 * Navigation (navigation-design 2026-10-25; the server decides, the app reports)
 *   nav_door            {door, mystic}          once per device, the first visit: organic | gift | mystic | lesson | ask
 *   nav_stage           {stage, door, mystic, trigger}  the server's stage_event: a stage first reached (once per account per stage)
 *   nav_unlock_seen     {key}                   a constellation's reveal landed in the bar (first_star | the_rising | venus | zodiac_wheel | the_moon | the_sun)
 *   nav_line_seen       {key}                   the Mystic's one room line was said (one_more | community_open)
 *   nav_tab             {item, stage}           a tap on the bar (altar | mystic | community | quest | studio)
 *   nav_next            {star, action, stage}   the Quest's Next card button
 *   nav_claim_all       {total, count}          "Claim N ✦" on the Quest sky claimed
 *
 * Money
 *   begin_checkout / purchase  {transaction_id, value, currency, items}
 *   mystic_seat_opened  {plan}
 *
 * Mystics and community
 *   tutorial_begin / tutorial_complete   the Create-a-Mystic onboarding
 *   become_mystic_step  {step}           each step of the Studio wizard
 *   become_mystic       {method, reader}
 *   publish_game        {skill}
 *   follow_reader / unfollow_reader {reader}
 *   home_mystic {reader}   (the Mystics tab: "Make this my home Mystic")
 *   friend_request_sent / friend_request_accepted
 *   poll_vote {poll}, candidate_generated, candidate_submitted
 *   quest_reward_claimed {quest}
 */
import { watch } from 'vue'
import { virtualPage, documentTitle } from '@/utils/analytics-pages'

// The address the visitor arrived on, before the app rewrites the query
// (logins, reading exits and panel syncs replace it). Its utm_* tags are what
// the visit is attributed to, so the first page_view must carry it.
const LANDING_HREF = typeof window !== 'undefined' ? window.location.href : ''
let landingSent = false
let lastPath = ''
const pending = new Set()

function gtagFn() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' ? window.gtag : null
}

function clean(params) {
  const out = {}
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== '') out[k] = v
  }
  return out
}

// Secrets that ride in our URLs: the login token on the activation link,
// invite and sign-in tokens on care packages, session and querent ids. GA
// gets the key with its value replaced. Kept in step with the inline guard in
// index.html / public/index.html (ga-inline-redact.spec.js checks they match).
const SECRET_PARAMS = /^(token|signin|keep|inv|birthday_request|session_id|querent_id|qid|code|key|email|password|otp|auth)$/i
export function redactUrl(url) {
  if (!url) return url
  try {
    const u = new URL(url)
    let changed = false
    for (const k of [...u.searchParams.keys()]) {
      if (SECRET_PARAMS.test(k)) { u.searchParams.set(k, 'REDACTED'); changed = true }
    }
    return changed ? u.toString() : url
  } catch (e) { return url }
}

export function track(event, params = {}) {
  try {
    const gtag = gtagFn()
    if (!gtag) return
    const p = clean(params)
    // While GA is live, remember the hit until Google confirms it, so a full
    // page navigation right after can wait for it (settle()).
    if (typeof window !== 'undefined' && window.__maGaActive) {
      let done
      const sent = new Promise((resolve) => { done = resolve })
      pending.add(sent)
      sent.then(() => pending.delete(sent))
      p.event_callback = done
      p.event_timeout = 1500
      setTimeout(done, 1600)
    }
    gtag('event', event, p)
  } catch (e) { /* analytics never breaks the app */ }
}

// GA4's recommended `share` event.
export function trackShare(method, contentType, itemId, extra = {}) {
  track('share', { method, content_type: contentType, item_id: itemId, ...extra })
}

// The seeker switched a reading between Public and Private on the share sheet.
export function trackReadingVisibility(isShared, itemId) {
  track(isShared ? 'reading_made_public' : 'reading_made_private', { item_id: itemId })
}

// Fire once per key, ever (per browser): a reading resumed after a reload or
// re-announced by the socket must not count twice.
const ONCE_KEY = 'ma:analytics:once'
export function trackOnce(key, event, params = {}) {
  let seen = []
  try { seen = JSON.parse(localStorage.getItem(ONCE_KEY) || '[]') } catch (e) { seen = [] }
  if (!Array.isArray(seen)) seen = []
  if (seen.includes(key)) return false
  seen.push(key)
  try { localStorage.setItem(ONCE_KEY, JSON.stringify(seen.slice(-300))) } catch (e) { /* no storage */ }
  track(event, params)
  return true
}

// Readings the app LOADED (a shared link, the journal, history) rather than
// performed: the socket announces them like a live one, and they must not
// count as reading_start / reading_complete.
const viewedReadings = new Set()
export function markReadingViewed(id) { if (id) viewedReadings.add(String(id)) }
export function isReadingViewed(id) { return !!id && viewedReadings.has(String(id)) }

// Wait (briefly) for queued hits before a full page navigation.
export function settle(timeoutMs = 1200) {
  if (!pending.size) return Promise.resolve()
  return Promise.race([
    Promise.all([...pending]),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ])
}

export function pageView(route) {
  try {
    const page = virtualPage(route)
    if (typeof document !== 'undefined') document.title = documentTitle(page)
    if (page.path === lastPath) return
    lastPath = page.path
    const gtag = gtagFn()
    if (!gtag) return
    let location = typeof window !== 'undefined' && route && route.fullPath
      ? window.location.origin + route.fullPath : ''
    if (!landingSent) { landingSent = true; location = LANDING_HREF || location }
    location = redactUrl(location)
    // Every event GA sends carries the page URL, not just page views: set the
    // redacted one globally so scrolls, clicks and our events carry it too.
    gtag('set', { page_location: location })
    gtag('event', 'page_view', clean({
      page_path: page.path, page_title: page.title, page_location: location,
    }))
  } catch (e) { /* analytics never breaks the app */ }
}

export function setUser(querentId, { isMystic } = {}) {
  try {
    const gtag = gtagFn()
    if (!gtag) return
    gtag('set', { user_id: querentId ? String(querentId) : null })
    if (isMystic !== undefined) gtag('set', 'user_properties', { is_mystic: isMystic ? 'yes' : 'no' })
  } catch (e) { /* analytics never breaks the app */ }
}

// GA user_id follows the signed-in account, wherever sign-in happens (login,
// registration, a claimed Mystic, a care-package door, a restored session),
// and clears when it does. An opaque id only, never the email. The store
// restores the account only once ReaderPage boots, after the router's first
// page view: the id is seeded from storage so that view is attributed too.
export function bindUserId(store) {
  let bootQuerent = ''
  try { bootQuerent = localStorage.getItem('querent_id') || '' } catch (e) { /* no storage */ }
  if (bootQuerent) setUser(bootQuerent)
  return watch(() => [store.querent_id, store.isReader], ([id, isReader], [prevId] = []) => {
    if (id) setUser(id, isReader !== null && isReader !== undefined ? { isMystic: !!isReader } : {})
    else if (prevId) setUser('')   // signed out (or the token expired)
  })
}

// Test seam: forget page/landing state between specs.
export function _resetForTests() { landingSent = false; lastPath = ''; pending.clear(); viewedReadings.clear() }
